import React, { useCallback } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import { Volume2 } from 'lucide-react';
import { speakTamil } from '../../utils/tts';

interface PhonicsCardItem {
  letter?: string;
  formula?: string;
  word?: string;
  gloss?: string;
  tts?: string;
  word_tts?: string;
  [k: string]: unknown;
}

interface PhonicsCardsV1Props {
  page: PageDetailOut;
}

function getCards(page: PageDetailOut): PhonicsCardItem[] {
  const content = page.page_json_v1?.content as { cards?: PhonicsCardItem[] } | undefined;
  return content?.cards ?? [];
}

export const PhonicsCardsV1: React.FC<PhonicsCardsV1Props> = ({ page }) => {
  const cards = getCards(page);
  const pageTitle =
    (page.page_title ?? (page.page_json_v1 as { page_title?: string })?.page_title ?? 'Phonics Cards') as string;

  const playLetter = useCallback((tts: string | undefined) => {
    if (tts?.trim()) speakTamil(tts.trim());
  }, []);

  const playWord = useCallback((word_tts: string | undefined) => {
    if (word_tts?.trim()) speakTamil(word_tts.trim());
  }, []);

  if (cards.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{pageTitle}</h1>
        <p className="mt-4 text-gray-500 dark:text-gray-400">No cards in this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{pageTitle}</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card, i) => {
          const letter = card.letter ?? '';
          const formula = card.formula ?? '';
          const word = (card.word ?? '').trim();
          const gloss = (card.gloss ?? '').trim();
          const tts = (card.tts ?? card.letter ?? '').trim();
          const word_tts = (card.word_tts ?? card.word ?? '').trim();
          const hasWord = word.length > 0;

          return (
            <div
              key={typeof card.id === 'string' || typeof card.id === 'number' ? card.id : `card-${i}`}
              className="rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 shadow-sm overflow-hidden flex flex-col"
            >
              <div className="p-5 flex flex-col items-center text-center space-y-3">
                {letter && (
                  <div className="text-4xl font-bold text-slate-900 dark:text-white">{letter}</div>
                )}
                {formula && (
                  <div className="text-lg font-medium text-slate-600 dark:text-slate-300">{formula}</div>
                )}
                {hasWord && (
                  <div className="space-y-1">
                    <div className="text-xl font-semibold text-slate-900 dark:text-white">{word}</div>
                    {gloss && (
                      <div className="text-sm text-slate-500 dark:text-slate-400">{gloss}</div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-3 border-t border-slate-200 dark:border-slate-600 flex items-center justify-center gap-3 bg-slate-50/50 dark:bg-slate-800/50">
                <button
                  type="button"
                  onClick={() => playLetter(tts)}
                  disabled={!tts}
                  aria-label={`Play letter: ${letter || tts}`}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 hover:bg-sky-200 dark:hover:bg-sky-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Volume2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Letter</span>
                </button>
                {hasWord && (
                  <button
                    type="button"
                    onClick={() => playWord(word_tts)}
                    disabled={!word_tts}
                    aria-label={`Play word: ${word}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Volume2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Word</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
